import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import Cropper from 'react-easy-crop';
import {
  FiAlertTriangle,
  FiArrowRight,
  FiBookOpen,
  FiBriefcase,
  FiCheckCircle,
  FiCrop,
  FiDroplet,
  FiGlobe,
  FiHeart,
  FiImage,
  FiLayers,
  FiMessageSquare,
  FiMic,
  FiPenTool,
  FiSave,
  FiStar,
  FiTag,
  FiTarget,
  FiTrash,
  FiUpload,
  FiUser,
  FiUsers,
} from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import CancelButton from '../../Components/Buttons/CancelButton';
import OutlinedDangerButton from '../../Components/Buttons/OutlinedDangerButton';
import PrimaryButton from '../../Components/Buttons/PrimaryButton';
import PageHeader from '../../Components/PageHeader';
import PermissionNotice from '../../Components/PermissionNotice';
import { BrandSettingsFormSkeleton } from '../../Components/SkeletonLoaders';
import { useGlobal } from '../../Context/GlobalContext';
import { useLanguage } from '../../Context/LanguageContext';
import { useWorkspace } from '../../Context/WorkspaceContext';
import '../../Onboarding/Onboarding.css';
import '../../Onboarding/components/SetupModal.css';
import { useWorkspacePermissions } from '../../hooks/useWorkspacePermissions';
import '../Account/account.css';
import './BrandSettings.css';

// Import shared components from Onboarding
import {
  ColorPickerField,
  MultiSelectField,
  SingleChoiceField,
} from '../../Onboarding/components/FormFields';
import PackModal from '../../Onboarding/components/PackModal';

// Helper: Get cropped image blob function for react-easy-crop
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

async function getCroppedImg(
  imageSrc,
  croppedAreaPixels,
  fileName = 'cropped-image.png'
) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) throw new Error('Failed to get 2D context');

  const pixelRatio = window.devicePixelRatio || 1;

  canvas.width = croppedAreaPixels.width * pixelRatio;
  canvas.height = croppedAreaPixels.height * pixelRatio;

  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = 'high';

  ctx.drawImage(
    image,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    croppedAreaPixels.width,
    croppedAreaPixels.height
  );

  // Apply circular mask
  ctx.globalCompositeOperation = 'destination-in';
  ctx.beginPath();
  ctx.arc(
    croppedAreaPixels.width / 2,
    croppedAreaPixels.height / 2,
    croppedAreaPixels.width / 2,
    0,
    Math.PI * 2
  );
  ctx.closePath();
  ctx.fill();
  ctx.globalCompositeOperation = 'source-over';

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to create blob from canvas.'));
          return;
        }
        blob.name = fileName;
        const fileUrl = URL.createObjectURL(blob);
        resolve({ blob, fileUrl });
      },
      'image/png',
      1
    );
  });
}

const BrandSettings = () => {
  const { t, lang } = useLanguage();
  const { apiUrl } = useGlobal();
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = useWorkspacePermissions();
  useWorkspace();

  // Get current section from URL path
  const getCurrentSection = () => {
    const pathParts = location.pathname.split('/');
    const lastPart = pathParts[pathParts.length - 1];
    // Map valid sections, default to 'basics'
    if (['basics', 'identity', 'presence', 'packs'].includes(lastPart)) {
      return lastPart;
    }
    return 'basics';
  };
  const currentSection = getCurrentSection();

  // Brand Type State
  const [brandType, setBrandType] = useState('company');

  // Pack Modal State
  const [activePack, setActivePack] = useState(null);
  const [isSavingPack, setIsSavingPack] = useState(false);

  // Form Data State - All fields from Onboarding
  const [formData, setFormData] = useState({
    // Identity & Core
    company_name: '',
    brand_type: 'company',
    workspace_language: lang || 'en',
    organization_type: '',
    creator_type: '',
    industry: [],
    primary_niche: '',

    // Presence & Offerings
    products_services: [],
    platforms: [],

    // Visuals & Story
    logo: null,
    main_color: '#55297f',
    secondary_color: '',
    accent_color: '',
    tagline: '',
    brand_story: '',

    // Personality Pack
    brand_archetype: [],
    formality_level: '',
    voice_tone_keywords: [],
    role_in_customer_journey: [],
    risk_appetite: '',
    creator_role: [],
    personality_traits: [],
    tone_of_voice: '',
    boundaries: [],

    // Audience Pack
    ideal_customer_type: [],
    target_customer_industries: [],
    target_regions: [],
    ideal_company_size: [],
    main_buyer_roles: [],
    key_pain_points: [],
    relationship_style: '',
    audience_age_ranges: [],
    audience_interests: [],
    audience_lifestyle: [],
    audience_values: [],

    // Style Pack
    content_formats: [],
    content_depth: '',
    content_energy: '',
    visual_style_keywords: [],
    brand_experience_focus: [],
    collaboration_interests: [],

    // Values Pack
    core_values: [],
    strategic_messaging_pillars: [],
    customer_emotions: [],
    messaging_red_lines: [],

    remove_logo: false,
  });

  const [originalData, setOriginalData] = useState({});
  const [logoPreview, setLogoPreview] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isConfirming, setIsConfirming] = useState(false);

  // Cropping State
  const [showCropModal, setShowCropModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [imgSrc, setImgSrc] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const fileInputRef = useRef(null);
  const [modalError, setModalError] = useState('');

  // State to track if there are changes
  const [hasChanges, setHasChanges] = useState(false);

  // --- Options Configuration (matching Onboarding) ---
  const companyOrgTypes = [
    {
      value: 'Startup (early or growth stage)',
      label: t('onboarding.orgTypes.startup'),
      description: t('onboarding.orgTypes.startupDesc'),
      icon: <FiBriefcase size={16} />,
    },
    {
      value: 'Small / Medium Business (SMB)',
      label: t('onboarding.orgTypes.smb'),
      description: t('onboarding.orgTypes.smbDesc'),
      icon: <FiBriefcase size={16} />,
    },
    {
      value: 'Enterprise / Large organization',
      label: t('onboarding.orgTypes.enterprise'),
      description: t('onboarding.orgTypes.enterpriseDesc'),
      icon: <FiBriefcase size={16} />,
    },
    {
      value: 'Agency / Consultancy',
      label: t('onboarding.orgTypes.agency'),
      description: t('onboarding.orgTypes.agencyDesc'),
      icon: <FiBriefcase size={16} />,
    },
    {
      value: 'Non-profit / NGO',
      label: t('onboarding.orgTypes.nonprofit'),
      description: t('onboarding.orgTypes.nonprofitDesc'),
      icon: <FiHeart size={16} />,
    },
    {
      value: 'Public institution / Government-related',
      label: t('onboarding.orgTypes.public'),
      description: t('onboarding.orgTypes.publicDesc'),
      icon: <FiBriefcase size={16} />,
    },
  ];

  const industries = [
    {
      value: 'Technology / SaaS',
      label: t('onboarding.industries.techSaas'),
      description: t('onboarding.industries.techSaasDesc'),
      icon: <FiLayers size={16} />,
    },
    {
      value: 'E-commerce & Online Retail',
      label: t('onboarding.industries.ecommerce'),
      description: t('onboarding.industries.ecommerceDesc'),
      icon: <FiGlobe size={16} />,
    },
    {
      value: 'Professional Services',
      label: t('onboarding.industries.profServices'),
      description: t('onboarding.industries.profServicesDesc'),
      icon: <FiBriefcase size={16} />,
    },
    {
      value: 'Manufacturing & Engineering',
      label: t('onboarding.industries.manufacturing'),
      description: t('onboarding.industries.manufacturingDesc'),
      icon: <FiLayers size={16} />,
    },
    {
      value: 'Finance, Banking & Fintech',
      label: t('onboarding.industries.finance'),
      description: t('onboarding.industries.financeDesc'),
      icon: <FiLayers size={16} />,
    },
    {
      value: 'Healthcare & Wellness',
      label: t('onboarding.industries.healthcare'),
      description: t('onboarding.industries.healthcareDesc'),
      icon: <FiHeart size={16} />,
    },
    {
      value: 'Education & Training',
      label: t('onboarding.industries.education'),
      description: t('onboarding.industries.educationDesc'),
      icon: <FiBookOpen size={16} />,
    },
    {
      value: 'Media, Marketing & Creative',
      label: t('onboarding.industries.media'),
      description: t('onboarding.industries.mediaDesc'),
      icon: <FiImage size={16} />,
    },
    {
      value: 'Hospitality, Travel & Tourism',
      label: t('onboarding.industries.travel'),
      description: t('onboarding.industries.travelDesc'),
      icon: <FiGlobe size={16} />,
    },
    {
      value: 'Public Sector / Government',
      label: t('onboarding.industries.government'),
      description: t('onboarding.industries.governmentDesc'),
      icon: <FiBriefcase size={16} />,
    },
  ];

  const productsServices = [
    {
      value: 'Digital products (software, apps, SaaS)',
      label: t('onboarding.productsServices.digitalSaas'),
      description: t('onboarding.productsServices.digitalSaasDesc'),
      icon: <FiLayers size={16} />,
    },
    {
      value: 'Physical products / hardware',
      label: t('onboarding.productsServices.physical'),
      description: t('onboarding.productsServices.physicalDesc'),
      icon: <FiLayers size={16} />,
    },
    {
      value: 'Consulting & advisory',
      label: t('onboarding.productsServices.consulting'),
      description: t('onboarding.productsServices.consultingDesc'),
      icon: <FiMessageSquare size={16} />,
    },
    {
      value: 'Implementation / integration services',
      label: t('onboarding.productsServices.implementation'),
      description: t('onboarding.productsServices.implementationDesc'),
      icon: <FiLayers size={16} />,
    },
    {
      value: 'Managed services / outsourcing',
      label: t('onboarding.productsServices.managed'),
      description: t('onboarding.productsServices.managedDesc'),
      icon: <FiLayers size={16} />,
    },
    {
      value: 'Training & education',
      label: t('onboarding.productsServices.training'),
      description: t('onboarding.productsServices.trainingDesc'),
      icon: <FiBookOpen size={16} />,
    },
    {
      value: 'Support & maintenance',
      label: t('onboarding.productsServices.support'),
      description: t('onboarding.productsServices.supportDesc'),
      icon: <FiHeart size={16} />,
    },
    {
      value: 'Licensing / subscriptions',
      label: t('onboarding.productsServices.licensing'),
      description: t('onboarding.productsServices.licensingDesc'),
      icon: <FiTag size={16} />,
    },
    {
      value: 'Events, workshops or experiences',
      label: t('onboarding.productsServices.events'),
      description: t('onboarding.productsServices.eventsDesc'),
      icon: <FiUsers size={16} />,
    },
  ];

  const companyPlatforms = [
    {
      value: 'Corporate website',
      label: t('onboarding.companyPlatforms.website'),
      description: t('onboarding.companyPlatforms.websiteDesc'),
      icon: <FiGlobe size={16} />,
    },
    {
      value: 'LinkedIn Company Page',
      label: t('onboarding.companyPlatforms.linkedin'),
      description: t('onboarding.companyPlatforms.linkedinDesc'),
      icon: <FiBriefcase size={16} />,
    },
    {
      value: 'Instagram',
      label: t('onboarding.companyPlatforms.instagram'),
      description: t('onboarding.companyPlatforms.instagramDesc'),
      icon: <FiImage size={16} />,
    },
    {
      value: 'YouTube',
      label: t('onboarding.companyPlatforms.youtube'),
      description: t('onboarding.companyPlatforms.youtubeDesc'),
      icon: <FiLayers size={16} />,
    },
    {
      value: 'X (Twitter)',
      label: t('onboarding.companyPlatforms.twitter'),
      description: t('onboarding.companyPlatforms.twitterDesc'),
      icon: <FiMessageSquare size={16} />,
    },
    {
      value: 'Facebook',
      label: t('onboarding.companyPlatforms.facebook'),
      description: t('onboarding.companyPlatforms.facebookDesc'),
      icon: <FiUsers size={16} />,
    },
    {
      value: 'TikTok',
      label: t('onboarding.companyPlatforms.tiktok'),
      description: t('onboarding.companyPlatforms.tiktokDesc'),
      icon: <FiLayers size={16} />,
    },
    {
      value: 'Email newsletter',
      label: t('onboarding.companyPlatforms.newsletter'),
      description: t('onboarding.companyPlatforms.newsletterDesc'),
      icon: <FiMessageSquare size={16} />,
    },
    {
      value: 'Partner marketplaces / app stores',
      label: t('onboarding.companyPlatforms.marketplaces'),
      description: t('onboarding.companyPlatforms.marketplacesDesc'),
      icon: <FiLayers size={16} />,
    },
    {
      value: 'Offline events & conferences',
      label: t('onboarding.companyPlatforms.offlineEvents'),
      description: t('onboarding.companyPlatforms.offlineEventsDesc'),
      icon: <FiUsers size={16} />,
    },
  ];

  const creatorTypes = [
    {
      value: 'Individual creator',
      label: t('onboarding.creatorTypes.individual'),
      description: t('onboarding.creatorTypes.individualDesc'),
      icon: <FiUser size={16} />,
    },
    {
      value: 'Duo / Team channel',
      label: t('onboarding.creatorTypes.team'),
      description: t('onboarding.creatorTypes.teamDesc'),
      icon: <FiUsers size={16} />,
    },
    {
      value: 'Company / Organization',
      label: t('onboarding.creatorTypes.organization'),
      description: t('onboarding.creatorTypes.organizationDesc'),
      icon: <FiBriefcase size={16} />,
    },
  ];

  const niches = [
    {
      value: 'Education & How-to',
      label: t('onboarding.niches.education'),
      description: t('onboarding.niches.educationDesc'),
      icon: <FiBookOpen size={16} />,
    },
    {
      value: 'Entertainment & Comedy',
      label: t('onboarding.niches.entertainment'),
      description: t('onboarding.niches.entertainmentDesc'),
      icon: <FiHeart size={16} />,
    },
    {
      value: 'Gaming',
      label: t('onboarding.niches.gaming'),
      description: t('onboarding.niches.gamingDesc'),
      icon: <FiLayers size={16} />,
    },
    {
      value: 'Business & Finance',
      label: t('onboarding.niches.business'),
      description: t('onboarding.niches.businessDesc'),
      icon: <FiBriefcase size={16} />,
    },
    {
      value: 'Tech & AI',
      label: t('onboarding.niches.techAi'),
      description: t('onboarding.niches.techAiDesc'),
      icon: <FiLayers size={16} />,
    },
    {
      value: 'Health, Fitness & Wellness',
      label: t('onboarding.niches.health'),
      description: t('onboarding.niches.healthDesc'),
      icon: <FiHeart size={16} />,
    },
    {
      value: 'Sleep, Relaxation & ASMR',
      label: t('onboarding.niches.relaxation'),
      description: t('onboarding.niches.relaxationDesc'),
      icon: <FiHeart size={16} />,
    },
    {
      value: 'Parenting & Family',
      label: t('onboarding.niches.family'),
      description: t('onboarding.niches.familyDesc'),
      icon: <FiUsers size={16} />,
    },
    {
      value: 'Fashion & Beauty',
      label: t('onboarding.niches.fashion'),
      description: t('onboarding.niches.fashionDesc'),
      icon: <FiImage size={16} />,
    },
    {
      value: 'Travel & Lifestyle',
      label: t('onboarding.niches.travelLifestyle'),
      description: t('onboarding.niches.travelLifestyleDesc'),
      icon: <FiGlobe size={16} />,
    },
    {
      value: 'News & Commentary',
      label: t('onboarding.niches.news'),
      description: t('onboarding.niches.newsDesc'),
      icon: <FiMessageSquare size={16} />,
    },
    {
      value: 'Music & Performance',
      label: t('onboarding.niches.music'),
      description: t('onboarding.niches.musicDesc'),
      icon: <FiHeart size={16} />,
    },
  ];

  const creatorServices = [
    {
      value: 'Learn new skills',
      label: t('onboarding.creatorServices.teachSkills'),
      description: t('onboarding.creatorServices.teachSkillsDesc'),
      icon: <FiBookOpen size={16} />,
    },
    {
      value: 'Relax, sleep, or reduce stress',
      label: t('onboarding.creatorServices.relaxation'),
      description: t('onboarding.creatorServices.relaxationDesc'),
      icon: <FiHeart size={16} />,
    },
    {
      value: 'Get motivated / inspired',
      label: t('onboarding.creatorServices.motivation'),
      description: t('onboarding.creatorServices.motivationDesc'),
      icon: <FiHeart size={16} />,
    },
    {
      value: 'Stay informed',
      label: t('onboarding.creatorServices.newsInfo'),
      description: t('onboarding.creatorServices.newsInfoDesc'),
      icon: <FiMessageSquare size={16} />,
    },
    {
      value: 'Be entertained / have fun',
      label: t('onboarding.creatorServices.entertainmentFun'),
      description: t('onboarding.creatorServices.entertainmentFunDesc'),
      icon: <FiHeart size={16} />,
    },
    {
      value: 'Improve career or business',
      label: t('onboarding.creatorServices.career'),
      description: t('onboarding.creatorServices.careerDesc'),
      icon: <FiBriefcase size={16} />,
    },
    {
      value: 'Improve health / fitness',
      label: t('onboarding.creatorServices.healthFitness'),
      description: t('onboarding.creatorServices.healthFitnessDesc'),
      icon: <FiHeart size={16} />,
    },
    {
      value: 'Improve relationships / parenting',
      label: t('onboarding.creatorServices.relationships'),
      description: t('onboarding.creatorServices.relationshipsDesc'),
      icon: <FiUsers size={16} />,
    },
    {
      value: 'Discover products / recommendations',
      label: t('onboarding.creatorServices.discovery'),
      description: t('onboarding.creatorServices.discoveryDesc'),
      icon: <FiLayers size={16} />,
    },
  ];

  const creatorPlatforms = [
    {
      value: 'YouTube (long videos)',
      label: t('onboarding.creatorPlatforms.youtubeLong'),
      description: t('onboarding.creatorPlatforms.youtubeLongDesc'),
      icon: <FiLayers size={16} />,
    },
    {
      value: 'YouTube Shorts',
      label: t('onboarding.creatorPlatforms.youtubeShorts'),
      description: t('onboarding.creatorPlatforms.youtubeShortsDesc'),
      icon: <FiLayers size={16} />,
    },
    {
      value: 'Instagram (posts & Reels)',
      label: t('onboarding.creatorPlatforms.instagramReels'),
      description: t('onboarding.creatorPlatforms.instagramReelsDesc'),
      icon: <FiImage size={16} />,
    },
    {
      value: 'TikTok',
      label: t('onboarding.creatorPlatforms.tiktokShort'),
      description: t('onboarding.creatorPlatforms.tiktokShortDesc'),
      icon: <FiLayers size={16} />,
    },
    {
      value: 'X / Twitter',
      label: t('onboarding.creatorPlatforms.twitterX'),
      description: t('onboarding.creatorPlatforms.twitterXDesc'),
      icon: <FiMessageSquare size={16} />,
    },
    {
      value: 'LinkedIn',
      label: t('onboarding.creatorPlatforms.linkedinPro'),
      description: t('onboarding.creatorPlatforms.linkedinProDesc'),
      icon: <FiBriefcase size={16} />,
    },
    {
      value: 'Twitch',
      label: t('onboarding.creatorPlatforms.twitch'),
      description: t('onboarding.creatorPlatforms.twitchDesc'),
      icon: <FiLayers size={16} />,
    },
    {
      value: 'Podcast',
      label: t('onboarding.creatorPlatforms.podcast'),
      description: t('onboarding.creatorPlatforms.podcastDesc'),
      icon: <FiMessageSquare size={16} />,
    },
    {
      value: 'Webpage',
      label: t('onboarding.creatorPlatforms.websitePersonal'),
      description: t('onboarding.creatorPlatforms.websitePersonalDesc'),
      icon: <FiGlobe size={16} />,
    },
    {
      value: 'Newsletter / Email',
      label: t('onboarding.creatorPlatforms.newsletterEmail'),
      description: t('onboarding.creatorPlatforms.newsletterEmailDesc'),
      icon: <FiMessageSquare size={16} />,
    },
  ];

  // Tooltip Text
  const tooltips = {
    name:
      brandType === 'company'
        ? t('onboarding.tooltips.companyName') ||
          'Legal or public-facing name of your company or main brand that customers recognize.'
        : t('onboarding.tooltips.creatorName') ||
          'Company, personal brand, or channel name that your audience knows you by.',
    orgType:
      t('onboarding.tooltips.orgType') ||
      'Describe what type of organization you are so BV can match tone and positioning to your business context.',
    creatorType:
      t('onboarding.tooltips.creatorType') ||
      'Specify whether you are an individual creator, a team, or a company so BV can frame the voice and messaging appropriately.',
    industry:
      t('onboarding.tooltips.industry') ||
      'Select the main industries or sectors you operate in. This helps BV use relevant examples, terminology and trends.',
    niche:
      t('onboarding.tooltips.niche') ||
      'Choose the main niche that best describes your overall content theme.',
    products:
      brandType === 'company'
        ? t('onboarding.tooltips.products') ||
          'What do you actually sell or deliver to clients? Focus on a few clear, high-level categories.'
        : t('onboarding.tooltips.services') ||
          'What do you help your audience with?',
    platforms:
      brandType === 'company'
        ? t('onboarding.tooltips.platformsCompany') ||
          'Where is your company most active and visible? BV will prioritize these channels in content and examples.'
        : t('onboarding.tooltips.platformsCreator') || 'Where are you active?',
    logo:
      t('onboarding.tooltips.logo') ||
      'Upload the primary logo/avatar BV should use in mockups, visuals or brand examples.',
    colors:
      t('onboarding.tooltips.colors') ||
      'Define your main brand colors so templates and visuals stay on-brand.',
    story:
      t('onboarding.tooltips.story') ||
      'Short version of how you started, what you do today and why you exist.',
    tagline:
      t('onboarding.tooltips.tagline') ||
      'Your main slogan or tagline that expresses your promise in one line.',
  };

  // --- Effect to check for changes ---
  useEffect(() => {
    let changesDetected = false;

    if (!!formData.logo) {
      changesDetected = true;
    } else if (formData.remove_logo && !!originalData.logo) {
      changesDetected = true;
    }

    if (!changesDetected) {
      const fieldsToCompare = [
        'company_name',
        'brand_type',
        'main_color',
        'secondary_color',
        'accent_color',
        'organization_type',
        'creator_type',
        'primary_niche',
        'formality_level',
        'risk_appetite',
        'tone_of_voice',
        'relationship_style',
        'content_depth',
        'content_energy',
        'tagline',
        'brand_story',
      ];

      for (const field of fieldsToCompare) {
        const currentVal = formData[field] ?? '';
        const originalVal = originalData[field] ?? '';
        if (currentVal !== originalVal) {
          changesDetected = true;
          break;
        }
      }

      if (!changesDetected) {
        const arrayFields = [
          'industry',
          'products_services',
          'platforms',
          'brand_archetype',
          'voice_tone_keywords',
          'role_in_customer_journey',
          'creator_role',
          'personality_traits',
          'boundaries',
          'ideal_customer_type',
          'target_customer_industries',
          'target_regions',
          'ideal_company_size',
          'main_buyer_roles',
          'key_pain_points',
          'audience_age_ranges',
          'audience_interests',
          'audience_lifestyle',
          'audience_values',
          'content_formats',
          'visual_style_keywords',
          'brand_experience_focus',
          'collaboration_interests',
          'core_values',
          'strategic_messaging_pillars',
          'customer_emotions',
          'messaging_red_lines',
        ];
        for (const field of arrayFields) {
          const currentArr = [...(formData[field] || [])].sort();
          const originalArr = [...(originalData[field] || [])].sort();

          if (
            currentArr.length !== originalArr.length ||
            !currentArr.every((val, index) => val === originalArr[index])
          ) {
            changesDetected = true;
            break;
          }
        }
      }
    }

    setHasChanges(changesDetected);
  }, [formData, originalData]);

  // --- Fetch current settings ---
  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      setError('');
      const brandvoxToken = localStorage.getItem('brandvox_token');
      if (!brandvoxToken) {
        setError(t('branding.brandsettings.errorAuth'));
        setIsLoading(false);
        navigate('/login');
        return;
      }

      try {
        const workspaceId = localStorage.getItem('current_workspace_id');
        if (!workspaceId) {
          setError(t('branding.brandsettings.errorWorkspace'));
          setIsLoading(false);
          navigate('/login');
          return;
        }

        const response = await fetch(
          `${apiUrl}/api/v1/brand/get-brand?workspace_id=${workspaceId}`,
          {
            headers: { Authorization: `Bearer ${brandvoxToken}` },
          }
        );
        if (!response.ok)
          throw new Error(t('branding.brandsettings.errorFetchBrand'));

        const data = await response.json();

        // Initialize all array fields
        const initArrayField = (field) => data[field] || [];

        const initialFormData = {
          ...data,
          brand_type: data.brand_type || 'company',
          industry: initArrayField('industry'),
          products_services: initArrayField('products_services'),
          platforms: initArrayField('platforms'),
          brand_archetype: initArrayField('brand_archetype'),
          voice_tone_keywords: initArrayField('voice_tone_keywords'),
          role_in_customer_journey: initArrayField('role_in_customer_journey'),
          creator_role: initArrayField('creator_role'),
          personality_traits: initArrayField('personality_traits'),
          boundaries: initArrayField('boundaries'),
          ideal_customer_type: initArrayField('ideal_customer_type'),
          target_customer_industries: initArrayField(
            'target_customer_industries'
          ),
          target_regions: initArrayField('target_regions'),
          ideal_company_size: initArrayField('ideal_company_size'),
          main_buyer_roles: initArrayField('main_buyer_roles'),
          key_pain_points: initArrayField('key_pain_points'),
          audience_age_ranges: initArrayField('audience_age_ranges'),
          audience_interests: initArrayField('audience_interests'),
          audience_lifestyle: initArrayField('audience_lifestyle'),
          audience_values: initArrayField('audience_values'),
          content_formats: initArrayField('content_formats'),
          visual_style_keywords: initArrayField('visual_style_keywords'),
          brand_experience_focus: initArrayField('brand_experience_focus'),
          collaboration_interests: initArrayField('collaboration_interests'),
          core_values: initArrayField('core_values'),
          strategic_messaging_pillars: initArrayField(
            'strategic_messaging_pillars'
          ),
          customer_emotions: initArrayField('customer_emotions'),
          messaging_red_lines: initArrayField('messaging_red_lines'),
          logo: null,
          remove_logo: false,
        };

        setFormData(initialFormData);
        setOriginalData({ ...initialFormData, logo: data.logo || null });
        setBrandType(data.brand_type || 'company');

        if (data.logo) {
          setLogoPreview(data.logo);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSettings();
  }, [apiUrl, navigate, t]);

  // --- Handlers ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBrandTypeChange = (newType) => {
    if (newType === brandType) return;

    const clearedData = { ...formData, brand_type: newType };

    if (newType === 'company') {
      // Clear person-specific fields
      clearedData.creator_type = '';
      clearedData.primary_niche = '';
      clearedData.creator_role = [];
      clearedData.boundaries = [];
      clearedData.audience_age_ranges = [];
      clearedData.audience_interests = [];
      clearedData.audience_lifestyle = [];
      clearedData.audience_values = [];
      clearedData.collaboration_interests = [];
      clearedData.tone_of_voice = '';
    } else {
      // Clear company-specific fields
      clearedData.organization_type = '';
      clearedData.industry = [];
      clearedData.formality_level = '';
      clearedData.voice_tone_keywords = [];
      clearedData.role_in_customer_journey = [];
      clearedData.risk_appetite = '';
      clearedData.ideal_customer_type = [];
      clearedData.target_customer_industries = [];
      clearedData.ideal_company_size = [];
      clearedData.main_buyer_roles = [];
      clearedData.key_pain_points = [];
      clearedData.content_depth = '';
      clearedData.brand_experience_focus = [];
      clearedData.strategic_messaging_pillars = [];
    }

    setFormData(clearedData);
    setBrandType(newType);
  };

  // --- Cropping Handlers ---
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file.size > 3 * 1024 * 1024) {
        setError(t('branding.brandsettings.errorLogoSize'));
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      if (
        !['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'].includes(
          file.type
        )
      ) {
        setError(t('branding.brandsettings.errorLogoType'));
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      setError('');

      const fileUrl = URL.createObjectURL(file);
      setImgSrc(fileUrl);
      setShowCropModal(true);

      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropCancel = () => {
    setShowCropModal(false);
    setImgSrc('');
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const handleCropImage = async () => {
    if (!croppedAreaPixels || !imgSrc) {
      setError(t('branding.brandsettings.errorCrop'));
      return;
    }
    try {
      const originalFileName = `cropped_logo_${Date.now()}.png`;
      const { blob, fileUrl } = await getCroppedImg(
        imgSrc,
        croppedAreaPixels,
        originalFileName
      );
      const croppedFile = new File([blob], originalFileName, {
        type: blob.type,
      });

      if (logoPreview && logoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(logoPreview);
      }

      setLogoPreview(fileUrl);
      setFormData((prev) => ({
        ...prev,
        logo: croppedFile,
        remove_logo: false,
      }));
      handleCropCancel();
    } catch (e) {
      setError(t('branding.brandsettings.errorCropFailed'));
      handleCropCancel();
    }
  };

  const handleRemoveLogo = () => {
    if (logoPreview && logoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoPreview(null);
    setFormData((prev) => ({ ...prev, logo: null, remove_logo: true }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // --- Reset Handler ---
  const handleReset = () => {
    setFormData({
      ...originalData,
      logo: null,
      remove_logo: false,
    });
    setBrandType(originalData.brand_type || 'company');

    if (originalData.logo) {
      setLogoPreview(originalData.logo);
    } else {
      setLogoPreview(null);
    }

    setError('');
    setModalError('');

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // --- Pack Handlers ---
  const handlePackSave = async () => {
    setIsSavingPack(true);
    try {
      const token = localStorage.getItem('brandvox_token');
      const workspaceId = localStorage.getItem('current_workspace_id');

      const data = new FormData();
      data.append('workspace_id', workspaceId);

      // Send all form data (pack modal updates formData directly)
      Object.keys(formData).forEach((key) => {
        if (key === 'logo' || key === 'remove_logo') return;
        const value = formData[key];
        if (Array.isArray(value)) {
          value.forEach((item) => data.append(key, item));
        } else if (value !== null && value !== undefined && value !== '') {
          data.append(key, value);
        }
      });

      const response = await fetch(`${apiUrl}/api/v1/brand/update-brand`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: data,
      });

      if (!response.ok) throw new Error('Failed to update brand');

      // Update original data to match current form
      setOriginalData({ ...formData, logo: originalData.logo });
      setActivePack(null);
      setSuccess(t('branding.brandsettings.successUpdate'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSavingPack(false);
    }
  };

  // --- Submit Handler ---
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!permissions.canModifyBrandSettings) {
      setError(t('branding.brandsettings.errorPermission'));
      return;
    }

    if (!hasChanges) {
      return;
    }

    setError('');
    setModalError('');
    setSuccess('');
    setShowConfirmModal(true);
  };

  const handleConfirmSave = async (e) => {
    e.preventDefault();
    setIsConfirming(true);
    setModalError('');
    const brandvoxToken = localStorage.getItem('brandvox_token');

    const data = new FormData();
    const workspaceId = localStorage.getItem('current_workspace_id');
    data.append('workspace_id', workspaceId);

    // Append all form data
    Object.keys(formData).forEach((key) => {
      const currentValue = formData[key];

      if (key === 'logo' && currentValue instanceof File) {
        data.append(key, currentValue, currentValue.name);
      } else if (
        key === 'remove_logo' &&
        currentValue === true &&
        originalData.logo
      ) {
        data.append(key, 'true');
      } else if (key !== 'logo' && key !== 'remove_logo') {
        if (Array.isArray(currentValue)) {
          if (currentValue.length > 0) {
            currentValue.forEach((item) => data.append(key, item));
          }
        } else if (
          currentValue !== null &&
          currentValue !== undefined &&
          currentValue !== ''
        ) {
          data.append(key, currentValue);
        }
      }
    });

    try {
      const response = await fetch(`${apiUrl}/api/v1/brand/update-brand`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${brandvoxToken}` },
        body: data,
      });

      const result = await response.json();

      if (!response.ok) {
        setModalError(result.detail || 'Failed to update brand settings.');
        setIsConfirming(false);
        return;
      }

      setShowConfirmModal(false);
      setIsConfirming(false);
      setSuccess(t('branding.brandsettings.successUpdate'));

      // Update original data
      setOriginalData({ ...formData, logo: logoPreview });

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setModalError(
        err.message || 'An unexpected error occurred. Please try again.'
      );
      setIsConfirming(false);
    }
  };

  // --- Pack Completion Detection ---
  const getCompletedPacks = () => {
    const hasPersonalityData = () => {
      if (brandType === 'company') {
        return (
          formData.brand_archetype?.length > 0 ||
          formData.formality_level ||
          formData.voice_tone_keywords?.length > 0 ||
          formData.role_in_customer_journey?.length > 0 ||
          formData.risk_appetite
        );
      } else {
        return (
          formData.brand_archetype?.length > 0 ||
          formData.creator_role?.length > 0 ||
          formData.personality_traits?.length > 0 ||
          formData.tone_of_voice ||
          formData.boundaries?.length > 0
        );
      }
    };

    const hasAudienceData = () => {
      if (brandType === 'company') {
        return (
          formData.ideal_customer_type?.length > 0 ||
          formData.target_customer_industries?.length > 0 ||
          formData.target_regions?.length > 0 ||
          formData.ideal_company_size?.length > 0 ||
          formData.main_buyer_roles?.length > 0 ||
          formData.key_pain_points?.length > 0 ||
          formData.relationship_style
        );
      } else {
        return (
          formData.audience_age_ranges?.length > 0 ||
          formData.target_regions?.length > 0 ||
          formData.audience_interests?.length > 0 ||
          formData.audience_lifestyle?.length > 0 ||
          formData.audience_values?.length > 0 ||
          formData.relationship_style
        );
      }
    };

    const hasStyleData = () => {
      if (brandType === 'company') {
        return (
          formData.content_formats?.length > 0 ||
          formData.content_depth ||
          formData.content_energy ||
          formData.visual_style_keywords?.length > 0 ||
          formData.brand_experience_focus?.length > 0
        );
      } else {
        return (
          formData.content_formats?.length > 0 ||
          formData.content_energy ||
          formData.visual_style_keywords?.length > 0 ||
          formData.collaboration_interests?.length > 0
        );
      }
    };

    const hasValuesData = () => {
      if (brandType === 'company') {
        return (
          formData.core_values?.length > 0 ||
          formData.strategic_messaging_pillars?.length > 0 ||
          formData.customer_emotions?.length > 0 ||
          formData.messaging_red_lines?.length > 0
        );
      } else {
        return (
          formData.core_values?.length > 0 ||
          formData.customer_emotions?.length > 0 ||
          formData.messaging_red_lines?.length > 0
        );
      }
    };

    return {
      personality: hasPersonalityData(),
      audience: hasAudienceData(),
      style: hasStyleData(),
      values: hasValuesData(),
    };
  };

  const completedPacks = getCompletedPacks();
  const completedCount = Object.values(completedPacks).filter(Boolean).length;
  const progress = (completedCount / 4) * 100;

  // --- Pack Cards Configuration ---
  const packs = [
    {
      id: 'personality',
      title: t('onboarding.packPersonality') || 'Personality & Role',
      desc:
        t('onboarding.packPersonalityDesc') ||
        'Define how your brand sounds and behaves.',
      icon: <FiMic size={22} />,
    },
    {
      id: 'audience',
      title: t('onboarding.packAudience') || 'Audience & Market',
      desc: t('onboarding.packAudienceDesc') || 'Who are you talking to?',
      icon: <FiTarget size={22} />,
    },
    {
      id: 'style',
      title: t('onboarding.packStyle') || 'Content & Style',
      desc: t('onboarding.packStyleDesc') || 'Formats, visuals, and energy.',
      icon: <FiPenTool size={22} />,
    },
    {
      id: 'values',
      title: t('onboarding.packValues') || 'Values & Messaging',
      desc: t('onboarding.packValuesDesc') || 'What you stand for.',
      icon: <FiHeart size={22} />,
    },
  ];

  // --- Page Header Configuration per Section ---
  const getSectionHeaderConfig = () => {
    switch (currentSection) {
      case 'basics':
        return {
          icon: <FiTag size={20} />,
          title: t('branding.brandsettings.basicsTitle') || 'Brand Basics',
          description:
            t('branding.brandsettings.basicsDescription') ||
            'Your brand name, logo, colors, and story',
        };
      case 'identity':
        return {
          icon: <FiUser size={20} />,
          title: t('branding.brandsettings.identityTitle') || 'Identity & Core',
          description:
            t('branding.brandsettings.identityDescription') ||
            'Define the core identity of your brand',
        };
      case 'presence':
        return {
          icon: <FiGlobe size={20} />,
          title:
            t('branding.brandsettings.presenceTitle') || 'Presence & Offerings',
          description:
            t('branding.brandsettings.presenceDescription') ||
            'Where are you active and what do you offer?',
        };
      case 'packs':
        return {
          icon: <FiStar size={20} />,
          title:
            t('branding.brandsettings.enhancementTitle') || 'Enhancement Packs',
          description:
            t('branding.brandsettings.enhancementDescription') ||
            'Complete these packs to help our AI generate better content',
        };
      default:
        return {
          icon: <FiBriefcase size={20} />,
          title: t('branding.brandsettings.pageTitle') || 'Brand Settings',
          description:
            t('branding.brandsettings.pageDescription') ||
            'Configure your brand identity and preferences',
        };
    }
  };

  const sectionHeader = getSectionHeaderConfig();

  // --- Page Header ---
  // Don't show save/reset buttons on Enhancement Packs tab (saves via modal)
  const showFormButtons =
    currentSection !== 'packs' && permissions.canModifyBrandSettings;

  const pageHeaderComponent = (
    <PageHeader
      icon={sectionHeader.icon}
      title={sectionHeader.title}
      description={sectionHeader.description}
      cancelButton={
        showFormButtons
          ? {
              onClick: handleReset,
              disabled: !hasChanges || isConfirming,
              children: t('branding.brandsettings.resetChanges'),
            }
          : null
      }
      saveButton={
        showFormButtons
          ? {
              onClick: () =>
                document.getElementById('brand-settings-form')?.requestSubmit(),
              disabled: !hasChanges || isConfirming,
              loading: isConfirming,
              loadingText: t('branding.brandsettings.saving'),
              children: (
                <>
                  <FiSave className='me-1' />
                  {t('branding.brandsettings.saveChanges')}
                </>
              ),
              type: 'submit',
              form: 'brand-settings-form',
            }
          : null
      }
    />
  );

  if (isLoading) {
    return <BrandSettingsFormSkeleton pageHeader={pageHeaderComponent} />;
  }

  return (
    <div className='brand-settings-container'>
      {pageHeaderComponent}

      <div className='brand-settings-content'>
        <Form onSubmit={handleSubmit} id='brand-settings-form'>
          {/* Alerts */}
          {error && !modalError && (
            <Alert
              variant='danger'
              className='brand-settings-alert'
              onClose={() => setError('')}
              dismissible
            >
              <FiAlertTriangle className='me-2' />
              {error}
            </Alert>
          )}

          {/* Permission Notice */}
          {!permissions.canModifyBrandSettings && (
            <PermissionNotice
              type='view-only'
              message={t('branding.brandsettings.viewOnlyMessage')}
            />
          )}

          <fieldset
            disabled={!permissions.canModifyBrandSettings}
            className='brand-settings-fieldset'
          >
            {/* Basics Section - Brand Name, Logo, Colors, Tagline, Story */}
            {currentSection === 'basics' && (
              <div className='brand-settings-section mb-5'>
                {/* Brand Name */}
                <Form.Group className='mb-4'>
                  <Form.Label className='form-label-bold'>
                    <FiTag size={16} style={{ color: 'var(--primary)' }} />
                    <span>
                      {brandType === 'company'
                        ? t('onboarding.companyName') || 'Brand / Company Name'
                        : t('onboarding.creatorName') || 'Brand / Creator Name'}
                    </span>
                    <span className='text-danger'>*</span>
                  </Form.Label>
                  <p className='form-section-description'>{tooltips.name}</p>
                  <Form.Control
                    type='text'
                    name='company_name'
                    value={formData.company_name}
                    onChange={handleInputChange}
                    placeholder={
                      brandType === 'company'
                        ? t('onboarding.companyNamePlaceholder') ||
                          'Enter your brand or company name'
                        : t('onboarding.creatorNamePlaceholder') ||
                          'Enter your brand or channel name'
                    }
                    required
                    className='form-input-custom'
                  />
                </Form.Group>

                {/* Logo Upload */}
                <Form.Group className='mb-4'>
                  <Form.Label className='form-label-bold'>
                    <FiImage size={16} style={{ color: 'var(--primary)' }} />
                    <span>
                      {t('onboarding.brandLogoLabel') || 'Brand Logo'}
                    </span>
                  </Form.Label>
                  <p className='form-section-description'>{tooltips.logo}</p>

                  {!logoPreview ? (
                    <div
                      className='logo-upload-area'
                      onClick={() =>
                        fileInputRef.current && fileInputRef.current.click()
                      }
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onDragEnter={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const files = e.dataTransfer.files;
                        if (files && files.length > 0) {
                          const syntheticEvent = { target: { files } };
                          handleFileChange(syntheticEvent);
                        }
                      }}
                    >
                      <Form.Control
                        ref={fileInputRef}
                        type='file'
                        onChange={handleFileChange}
                        accept='image/png, image/jpeg, image/svg+xml, image/webp'
                        className='d-none'
                      />
                      <FiUpload
                        size={28}
                        className='mb-2'
                        style={{ color: 'var(--primary)' }}
                      />
                      <div className='text-muted small'>
                        <span
                          className='fw-bold'
                          style={{ color: 'var(--primary)' }}
                        >
                          {t('onboarding.logoUploadTitle') || 'Click to upload'}
                        </span>{' '}
                        {t('onboarding.logoUploadOr') || 'or drag and drop'}
                      </div>
                      <div
                        className='text-muted mt-1'
                        style={{ fontSize: '0.75rem' }}
                      >
                        {t('onboarding.logoUploadFormats') ||
                          'SVG, PNG, JPG (max 3MB)'}
                      </div>
                    </div>
                  ) : (
                    <div className='logo-preview-container d-flex align-items-center justify-content-between'>
                      <img
                        src={logoPreview}
                        alt='Logo Preview'
                        className='logo-preview'
                      />
                      <OutlinedDangerButton
                        size='sm'
                        onClick={handleRemoveLogo}
                      >
                        <FiTrash size={16} />
                      </OutlinedDangerButton>
                    </div>
                  )}
                </Form.Group>

                {/* Brand Colors */}
                <Form.Group className='mb-4'>
                  <Form.Label className='form-label-bold'>
                    <FiDroplet size={16} style={{ color: 'var(--primary)' }} />
                    <span>
                      {t('onboarding.brandColorsTitle') || 'Brand Colors'}
                    </span>
                  </Form.Label>
                  <p className='form-section-description'>{tooltips.colors}</p>
                  <Row className='g-3'>
                    <Col xs={12} sm={4}>
                      <ColorPickerField
                        label={t('onboarding.colorPrimary') || 'Primary'}
                        name='main_color'
                        value={formData.main_color}
                        onChange={handleInputChange}
                      />
                    </Col>
                    <Col xs={6} sm={4}>
                      <ColorPickerField
                        label={t('onboarding.colorSecondary') || 'Secondary'}
                        name='secondary_color'
                        value={formData.secondary_color}
                        onChange={handleInputChange}
                        placeholder='#Optional'
                      />
                    </Col>
                    <Col xs={6} sm={4}>
                      <ColorPickerField
                        label={t('onboarding.colorAccent') || 'Accent'}
                        name='accent_color'
                        value={formData.accent_color}
                        onChange={handleInputChange}
                        placeholder='#Optional'
                      />
                    </Col>
                  </Row>
                </Form.Group>

                {/* Tagline */}
                <Form.Group className='mb-4'>
                  <Form.Label className='form-label-bold'>
                    <FiMessageSquare
                      size={16}
                      style={{ color: 'var(--primary)' }}
                    />
                    <span>{t('onboarding.tagline') || 'Tagline / Slogan'}</span>
                  </Form.Label>
                  <p className='form-section-description'>{tooltips.tagline}</p>
                  <Form.Control
                    type='text'
                    name='tagline'
                    value={formData.tagline}
                    onChange={handleInputChange}
                    placeholder={
                      t('onboarding.taglinePlaceholderNew') ||
                      'Your main slogan or tagline'
                    }
                    className='form-input-custom'
                  />
                </Form.Group>

                {/* Brand Story */}
                <Form.Group className='mb-4'>
                  <Form.Label className='form-label-bold'>
                    <FiBookOpen size={16} style={{ color: 'var(--primary)' }} />
                    <span>{t('onboarding.brandStory') || 'Brand Story'}</span>
                  </Form.Label>
                  <p className='form-section-description'>{tooltips.story}</p>
                  <Form.Control
                    as='textarea'
                    rows={3}
                    name='brand_story'
                    value={formData.brand_story}
                    onChange={handleInputChange}
                    placeholder={
                      t('onboarding.brandStoryPlaceholder') ||
                      'Brief narrative about your brand (origin, mission, what makes you unique)'
                    }
                    className='form-input-custom'
                  />
                </Form.Group>
              </div>
            )}

            {/* Identity Section - Org Type/Creator Type + Industry/Niche */}
            {currentSection === 'identity' && (
              <div className='brand-settings-section mb-5'>
                {/* Organization Type / Creator Type */}
                <SingleChoiceField
                  label={
                    brandType === 'company'
                      ? t('onboarding.orgType') || 'Organization Type'
                      : t('onboarding.creatorType') || 'Creator Type'
                  }
                  name={
                    brandType === 'company'
                      ? 'organization_type'
                      : 'creator_type'
                  }
                  value={
                    brandType === 'company'
                      ? formData.organization_type
                      : formData.creator_type
                  }
                  options={
                    brandType === 'company' ? companyOrgTypes : creatorTypes
                  }
                  onChange={handleInputChange}
                  description={
                    brandType === 'company'
                      ? tooltips.orgType
                      : tooltips.creatorType
                  }
                  icon={
                    brandType === 'company' ? (
                      <FiBriefcase size={16} />
                    ) : (
                      <FiUser size={16} />
                    )
                  }
                />

                {/* Industry / Niche */}
                {brandType === 'company' ? (
                  <MultiSelectField
                    label={t('onboarding.industry') || 'Industry / Sector'}
                    name='industry'
                    value={formData.industry}
                    options={industries}
                    onChange={handleInputChange}
                    max={3}
                    description={tooltips.industry}
                    icon={<FiLayers size={16} />}
                  />
                ) : (
                  <MultiSelectField
                    label={t('onboarding.niche') || 'Primary Niche'}
                    name='primary_niche'
                    value={
                      formData.primary_niche ? [formData.primary_niche] : []
                    }
                    options={niches}
                    onChange={(e) =>
                      handleInputChange({
                        target: {
                          name: 'primary_niche',
                          value: e.target.value[0] || '',
                        },
                      })
                    }
                    max={1}
                    description={tooltips.niche}
                    allowOther={false}
                    icon={<FiTag size={16} />}
                  />
                )}
              </div>
            )}

            {/* Presence & Offerings Section */}
            {currentSection === 'presence' && (
              <div className='brand-settings-section mb-5'>
                <MultiSelectField
                  label={
                    brandType === 'company'
                      ? t('onboarding.products') || 'Main Products / Services'
                      : t('onboarding.services') || 'Main Services / Value'
                  }
                  name='products_services'
                  value={formData.products_services}
                  options={
                    brandType === 'company' ? productsServices : creatorServices
                  }
                  onChange={handleInputChange}
                  max={brandType === 'company' ? 5 : 3}
                  description={tooltips.products}
                  icon={<FiLayers size={16} />}
                />

                <MultiSelectField
                  label={t('onboarding.platforms') || 'Main Platforms'}
                  name='platforms'
                  value={formData.platforms}
                  options={
                    brandType === 'company'
                      ? companyPlatforms
                      : creatorPlatforms
                  }
                  onChange={handleInputChange}
                  max={10}
                  description={tooltips.platforms}
                  icon={<FiGlobe size={16} />}
                />
              </div>
            )}

            {/* Enhancement Packs Section */}
            {currentSection === 'packs' && (
              <div className='brand-settings-section enhancement-hub'>
                {/* Progress */}
                <div className='enhancement-hub-progress mb-4'>
                  <div className='d-flex justify-content-between align-items-center mb-2'>
                    <span
                      className='small fw-semibold'
                      style={{ color: 'var(--text-secondary, #6b7280)' }}
                    >
                      {t('onboarding.profileStrength') || 'Profile Strength'}: {completedCount} / 4
                    </span>
                    {completedCount === 4 && (
                      <span
                        className='small fw-semibold'
                        style={{ color: '#10b981' }}
                      >
                        <FiCheckCircle size={14} className='me-1' />
                        {t('onboarding.allPacksComplete') || 'All complete!'}
                      </span>
                    )}
                  </div>
                  <div className='enhancement-hub-progress-bar'>
                    <div
                      className='enhancement-hub-progress-fill'
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>

                {/* Pack Cards */}
                <div className='enhancement-hub-cards'>
                  {packs.map((pack) => {
                    const isCompleted = completedPacks[pack.id];
                    return (
                      <div
                        key={pack.id}
                        className={`enhancement-hub-card ${isCompleted ? 'completed' : ''}`}
                        onClick={() => setActivePack(pack.id)}
                      >
                        <div
                          className={`enhancement-hub-card-icon ${isCompleted ? 'completed' : ''}`}
                        >
                          {isCompleted ? (
                            <FiCheckCircle size={20} />
                          ) : (
                            pack.icon
                          )}
                        </div>
                        <div className='enhancement-hub-card-content'>
                          <div className='enhancement-hub-card-title'>
                            {pack.title}
                          </div>
                          <div className='enhancement-hub-card-desc'>
                            {pack.desc}
                          </div>
                        </div>
                        <FiArrowRight
                          size={16}
                          className='enhancement-hub-card-arrow'
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </fieldset>
        </Form>
      </div>

      {/* Pack Modal */}
      {activePack && (
        <PackModal
          show={!!activePack}
          onHide={() => setActivePack(null)}
          packId={activePack}
          brandType={brandType}
          formData={formData}
          onChange={handleInputChange}
          onSave={handlePackSave}
          isSaving={isSavingPack}
        />
      )}

      {/* Cropping Modal */}
      <Modal show={showCropModal} onHide={handleCropCancel} centered size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>
            <FiCrop
              size={20}
              className='me-2'
              style={{ color: 'var(--primary)' }}
            />
            {t('branding.brandsettings.cropTitle') || 'Crop Logo'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0 }}>
          {imgSrc ? (
            <>
              <div
                style={{
                  position: 'relative',
                  height: '400px',
                  backgroundColor: '#f0f0f0',
                }}
              >
                <Cropper
                  image={imgSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape='round'
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  backgroundColor: '#ffffff',
                  padding: '16px 24px',
                  borderTop: '1px solid #e5e7eb',
                }}
              >
                <span
                  style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                  }}
                >
                  {t('branding.brandsettings.cropZoom') || 'Zoom'}
                </span>
                <input
                  type='range'
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  style={{ width: '240px', cursor: 'pointer' }}
                />
                <span
                  style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    minWidth: '40px',
                  }}
                >
                  {Math.round(zoom * 100)}%
                </span>
              </div>
            </>
          ) : (
            <div
              className='text-center p-4'
              style={{
                height: '400px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Spinner animation='border' variant='primary' />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <CancelButton onClick={handleCropCancel}>
            {t('branding.brandsettings.cropCancel') || 'Cancel'}
          </CancelButton>
          <PrimaryButton
            onClick={handleCropImage}
            disabled={!croppedAreaPixels}
          >
            {t('branding.brandsettings.cropSave') || 'Save'}
          </PrimaryButton>
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => !isConfirming && setShowConfirmModal(false)}
        centered
        backdrop={isConfirming ? 'static' : true}
        keyboard={!isConfirming}
      >
        <Modal.Header closeButton={!isConfirming}>
          <Modal.Title>
            {t('branding.brandsettings.confirmTitle') || 'Confirm Changes'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError && (
            <Alert variant='danger' className='d-flex align-items-center'>
              <FiAlertTriangle className='me-2' />
              {modalError}
            </Alert>
          )}
          {!modalError &&
            (t('branding.brandsettings.confirmMessage') ||
              'Are you sure you want to save these changes to your brand settings?')}
        </Modal.Body>
        <Modal.Footer>
          <CancelButton
            onClick={() => setShowConfirmModal(false)}
            disabled={isConfirming}
          >
            {t('branding.brandsettings.confirmCancel') || 'Cancel'}
          </CancelButton>
          <PrimaryButton onClick={handleConfirmSave} disabled={isConfirming}>
            {isConfirming ? (
              <>
                <Spinner
                  as='span'
                  animation='border'
                  size='sm'
                  role='status'
                  aria-hidden='true'
                  className='me-1'
                />
                {t('branding.brandsettings.confirming') || 'Saving...'}
              </>
            ) : (
              t('branding.brandsettings.confirmSave') || 'Save Changes'
            )}
          </PrimaryButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BrandSettings;
